import Close from "@mui/icons-material/Close";
import ExitModal from "components/NewForm/ExitModal";
import ArticleDetailContent from "components/shared/ArticleDetailsContent";
import ElementDetails from "components/shared/ElementDetails";
import ArticleFormContainer from "containers/ArticleFormContainer";
import { PortalType } from "contexts/portal.context";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { resetAgreement } from "store/agreementsStore";
import type { IArticle, ManagementArticle } from "store/articleStore";
import { useAppDispatch, useAppSelector } from "store/store";

interface IArticleDrawer {
    open: boolean;
    onClose: () => void;
    /**
     * Articles from the table, use to switch index with the two navigation buttons left to the drawer
     */
    articlesFromTable: IArticle[] | ManagementArticle[];
    /**
     * Current opened Article/Service state
     */
    currentArticle: IArticle | ManagementArticle | null | undefined;
    /**
     * Change current Article state
     */
    setCurrentArticle: any;
    /**
     * From which page is called article drawer
     */
    from: "beneficiary" | "publication" | "management";
    /**
     * Handle open agreement modal
     */
    handleShowAgreementModal: (article: IArticle) => void;
}
/**
 * This is Article/Service drawer for "press" or "audiovisual" company
 */
const ArticleDrawer = ({
    open,
    onClose,
    articlesFromTable,
    currentArticle,
    setCurrentArticle,
    from,
    handleShowAgreementModal,
}: IArticleDrawer) => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { sendAgreementStatus } = useAppSelector((state) => state.agreements.agFetchStatus);
    const defaultTab = location.state != null && location.state.tab ? location.state.tab : "0";
    const portalType = useContext(PortalType);

    const [articleEditMode, setArticleEditMode] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [blockModal, setBlockModal] = useState(false);
    const [currentTab, setCurrentTab] = useState(defaultTab);

    const articleIndex = useMemo(
        () => articlesFromTable.findIndex((art) => art.id == currentArticle?.id),
        [currentArticle],
    );

    /**
     * When update article (from form) update state data of the current article
     */
    useEffect(() => {
        setCurrentArticle((prev) => {
            if (!prev) return;
            const updatedArticle = articlesFromTable.find((art) => art.id === prev?.id);
            return updatedArticle;
        });
    }, [articlesFromTable]);

    /**
     * Change current article when switching article with arrows on the left
     */
    const handleCurrentArticle = (e: number) => {
        setCurrentArticle(articlesFromTable[e]);
    };

    /**
     * Close article when editing form
     */
    const handleCloseFormWithModal = () => {
        if (isDirty) setBlockModal(true);
        else setArticleEditMode(false);
    };

    /**
     * Handle close article drawer, different behavior if the edit form mode is active
     */
    const handleCloseArticleDrawer = () => {
        if (!articleEditMode) {
            handleClose();
        } else {
            handleCloseFormWithModal();
        }
    };

    const handleClose = () => {
        onClose();
        dispatch(resetAgreement());
        setCurrentTab(defaultTab);
    };

    /**
     * When agreement has been sent, reset tab state
     */
    useEffect(() => {
        if (sendAgreementStatus === "succeeded") {
            if (currentTab != "2") return;
            setCurrentTab("0");
        }
    }, [sendAgreementStatus]);

    const hideSwitchButtons = articleEditMode || articlesFromTable.length < 2;

    return (
        // Drawer with left arrows to switch between articles
        <ElementDetails
            open={open}
            onClose={handleCloseArticleDrawer}
            elements={articlesFromTable}
            setElementIndex={(e: number) => handleCurrentArticle(e)}
            elementIndex={articleIndex}
            hideSwitchButtons={hideSwitchButtons}
        >
            <>
                {!articleEditMode && (
                    <ArticleDetailContent
                        article={currentArticle}
                        onClose={handleClose}
                        editMode={articleEditMode}
                        onEditMode={() => setArticleEditMode(true)}
                        from={from}
                        handleShowAgreementModal={() => handleShowAgreementModal(currentArticle as IArticle)}
                        currentTab={currentTab}
                        onTabChange={(_, tab) => setCurrentTab(tab)}
                    />
                )}
                {["audiovisual", "press"].includes(portalType) && currentArticle && articleEditMode && (
                    <>
                        <Close className="edit-close-btn" onClick={handleCloseFormWithModal} />
                        <ArticleFormContainer
                            article={currentArticle}
                            mode="edit"
                            editModeActive={articleEditMode}
                            closeEditMode={() => setArticleEditMode(false)}
                            handleDirty={(isDirty: boolean) => setIsDirty(isDirty)}
                            from={from}
                            closeDrawer={() => {
                                handleClose();
                                setCurrentArticle(undefined);
                            }}
                        />
                        <ExitModal
                            open={blockModal}
                            onConfirm={() => {
                                setArticleEditMode(false);
                                setBlockModal(false);
                                setIsDirty(false);
                            }}
                            onCancel={() => setBlockModal(false)}
                        />
                    </>
                )}
            </>
        </ElementDetails>
    );
};

export default ArticleDrawer;
